import React, { useEffect, useRef, useState } from 'react';
import {
  Checkout,
  DraggableComponents,
} from '../../../../common/types/checkout';
import { ModernOrderSummary } from '../../../organisms/modern/modern-order-summary';
import { ExpressCard } from '../../../molecules/express-card/express-card';
import { useTranslation } from 'react-i18next';
import { CheckoutFormModern } from '../../../organisms/modern/checkout-form/checkout-form-modern.organism';
import { useCheckoutController } from '../../../../common/hooks/useCheckoutController/useCheckoutController';
import { MetodoPagamentoVendaEnum } from '../../../../common/types/enums/metodo-pagamento.enum';
import { CreatePurchaseContract } from '../../../../common/types/create-purchase-contract';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { EventState } from '../../../../common/redux/slices/event.slice';
import { MpCardSectionRef } from '../../../molecules/mp-card-section/mp-card-section';
import { StripeElementRef } from '../../../atoms/stripe-inputs/stripe-card-input/stripe-card-input';
import { StripeState } from '../../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { DeliveryFormModern } from '../../../organisms/modern/delivery-form/delivery-form-modern.organism';
import SSL from '../../.././../common/assets/ssl-secure.png';
import SecurePayment from '../../.././../common/assets/secure-payment.png';
import { PaymentFormModern } from '../../../organisms/modern/payment-form/payment-form.organism';
import * as S from './checkout-modern.page.style';
import { OrderBump } from '../../../organisms/order-bump/order-bump.organism';
import { CouponSection } from '../../../molecules/coupon-section/coupon-section.molecule';
import { Timer } from '../../../molecules/classic-checkout/timer/timer.molecule';
import { TextPreview } from '../../../atoms/classic-checkout/text/text.atom';
import { ClassicCheckoutImage } from '../../../atoms/classic-checkout/image/image.atom';
import { ClassicCheckoutDivider } from '../../../atoms/classic-checkout/divider/divider.atom';
import { CustomHtmlAtom } from '../../../atoms/classic-checkout/custom-html/custom-html.atom';
import { GuaranteeBadge } from '../../../molecules/classic-checkout/guarantee-badge/guarantee-badge.molecule';
import { SocialProofPreview } from '../../../molecules/classic-checkout/social-proof/social-proof.molecule';
import { LoadingScreen } from '../../../molecules/loading-screen/loading-screen.molecule';
import { CheckoutFormSkeleton } from '../../../molecules/checkout-form-skeleton/checkout-form-skeleton.molecule';
import { PaymentMethodsSkeleton } from '../../../molecules/payment-method-skeleton/payment-method-skeleton.molecule';
import { OrderSummaryClassicSkeleton } from '../../../molecules/order-summary-skeleton/order-summary-skeleton.molecule';

type Props = {
  checkout: Checkout;
  paymentMethod: MetodoPagamentoVendaEnum;
  setPaymentMethod: (pm: MetodoPagamentoVendaEnum) => void;
  onSubmit: (data: CreatePurchaseContract) => void;
  onToggleOrderBump: (applied: boolean, id: string) => void;
  isLoading: boolean;
  onApplyCoupon: (coupon: string) => void;
  couponMessage: string;
  discount: number;
  applePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  googlePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  amazonPay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  paypal?: {
    available: boolean;
    loading: boolean;
    currency: string;
    createOrder?: () => Promise<string>;
    createSubscription?: () => Promise<string>;
    onApprove: (data: { orderID: string; subscriptionID?: string }) => Promise<void>;
    onCancel: () => void;
    isSubscription?: boolean;
  };
};

export const CheckoutModern: React.FC<Props> = ({
  checkout,
  paymentMethod,
  setPaymentMethod,
  onSubmit,
  onToggleOrderBump,
  isLoading,
  onApplyCoupon,
  couponMessage,
  discount,
  applePay,
  googlePay,
  amazonPay,
  paypal,
}) => {
  const baselineLockedRef = React.useRef(false);
  const lastCheckoutIdRef = React.useRef<string | undefined>(undefined);
  const { t } = useTranslation();
  const alert = useAlert();
  const [openSummary, setOpenSummary] = useState(true);
  const eventId = useSelector((s: { event: EventState }) => s.event.eventId);
  const mpCardRef = useRef<MpCardSectionRef>(null);
  const numberRef = useRef<StripeElementRef>(null);
  const expiryRef = useRef<StripeElementRef>(null);
  const cvcRef = useRef<StripeElementRef>(null);
  const [desiredBumps, setDesiredBumps] = React.useState<
    Record<string, boolean>
  >({});
  const [stripeState, setStripeState] = React.useState<StripeState>({
    number: { complete: false, error: null },
    expiry: { complete: false, error: null },
    cvc: { complete: false, error: null },
  });
  const [baseSubtotal, setBaseSubtotal] = React.useState<number>(0);
  const handleOrderBumpToggle = (checked: boolean, id: string) => {
    setDesiredBumps((prev) => ({ ...prev, [id]: checked }));
    onToggleOrderBump(checked, id);
  };
  const { formData, handleChange, handlePurchaseClick } = useCheckoutController(
    {
      checkout,
      paymentMethod,
      onSubmit,
      t,
      alert,
      eventId,
      mpCardRef,
      stripeState,
      stripeNumberRef: numberRef,
      stripeExpiryRef: expiryRef,
      stripeCvcRef: cvcRef,
    },
  );

  const handleStripeChange = (
    field: 'number' | 'expiry' | 'cvc',
    s: { complete: boolean; error: string | null },
  ) => {
    setStripeState((prev) => ({ ...prev, [field]: s }));
  };

  const renderAditionalComponent = (component: DraggableComponents) => {
    switch (component.type) {
      case 'timer':
        return <Timer component={component} />;
      case 'text':
        const links = (component.style?.links ?? []) as Array<{
          id: string;
          start: number;
          end: number;
          href: string;
          newTab?: boolean;
        }>;

        const text = component.content ?? '';

        const safeLinks = [...links]
          .filter(
            (l) =>
              l &&
              l.start >= 0 &&
              l.end > l.start &&
              l.end <= text.length &&
              l.href?.trim(),
          )
          .sort((a, b) => a.start - b.start);

        const nodes: React.ReactNode[] = [];
        let cursor = 0;

        safeLinks.forEach((l) => {
          if (l.start > cursor) nodes.push(text.slice(cursor, l.start));

          const chunk = text.slice(l.start, l.end);
          nodes.push(
            <a
              key={l.id}
              href={l.href}
              target={l.newTab ? '_blank' : undefined}
              rel={l.newTab ? 'noopener noreferrer' : undefined}
              style={{
                textDecoration: 'underline',
                color: component.style.color,
              }}
            >
              {chunk}
            </a>,
          );

          cursor = l.end;
        });

        if (cursor < text.length) nodes.push(text.slice(cursor));

        return <TextPreview style={component.style}>{nodes}</TextPreview>;
      case 'image':
        return <ClassicCheckoutImage component={component} />;
      case 'divider':
        return <ClassicCheckoutDivider />;
      case 'advantages':
        return <GuaranteeBadge component={component} />;
      case 'social-proof':
        return <SocialProofPreview component={component} />;
      case 'custom-html':
        return <CustomHtmlAtom component={component} />;
    }
  };

  const { uiOrderBumpsAplicados, uiSubtotal } = React.useMemo(() => {
    const appliedList =
      (checkout?.orderBumps ?? []).filter((ob) => desiredBumps[ob.id]) ?? [];

    const bumpsSum = appliedList.reduce((acc, ob) => {
      const price = ob?.precos?.[0]?.valor ?? 0;
      return acc + price;
    }, 0);

    const subtotal = (baseSubtotal ?? 0) + bumpsSum;

    return {
      uiOrderBumpsAplicados: appliedList.map((ob) => ({
        id: ob.id,
        produtoOferta: ob.produtoOferta,
        precos: ob.precos,
      })),
      uiSubtotal: subtotal,
    };
  }, [checkout?.orderBumps, desiredBumps, baseSubtotal]);

  useEffect(() => {
    if (lastCheckoutIdRef.current !== checkout?.id) {
      baselineLockedRef.current = false;
      lastCheckoutIdRef.current = checkout?.id;
    }

    if (!checkout?.id || baselineLockedRef.current) return;
    const appliedSet = new Set(checkout?.orderBumpsAplicados?.map((o) => o.id));
    const initialDesired: Record<string, boolean> = {};
    (checkout?.orderBumps ?? []).forEach((ob) => {
      initialDesired[ob.id] = appliedSet.has(ob.id);
    });
    setDesiredBumps(initialDesired);
    const serverAppliedSum = (checkout?.orderBumpsAplicados ?? []).reduce(
      (acc, ob) => {
        const price = ob?.precos?.[0]?.valor ?? 0;
        return acc + price;
      },
      0,
    );
    setBaseSubtotal((checkout?.subtotal ?? 0) - serverAppliedSum);
    baselineLockedRef.current = true;
  }, [
    checkout?.id,
    checkout?.subtotal,
    checkout?.orderBumpsAplicados,
    checkout?.orderBumps,
  ]);

  const [qty, setQty] = useState(1);

  return (
    <>
      <S.Global
        blue={
          checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
            ?.background
        }
      />
      <S.Page>
        <div>
          {checkout?.checkoutConfig?.config?.componentsAbove?.map((c) =>
            renderAditionalComponent(c),
          )}
          {isLoading ? (
            <OrderSummaryClassicSkeleton />
          ) : (
            <ModernOrderSummary
              checkout={checkout}
              open={openSummary}
              setOpen={setOpenSummary}
              qty={qty}
              setQty={setQty}
              uiOrderBumpsAplicados={uiOrderBumpsAplicados}
              uiSubtotal={uiSubtotal}
              discount={discount}
            />
          )}

          {((checkout?.metodosPagamento?.includes('applepay') &&
            applePay?.available) ||
            (checkout?.metodosPagamento?.includes('googlepay') &&
              googlePay?.available) ||
            checkout?.metodosPagamento?.includes('amazonpay') ||
            paypal?.available) && (
            <ExpressCard
              applePay={applePay}
              googlePay={googlePay}
              amazonPay={amazonPay}
              paypal={paypal}
            />
          )}
          {isLoading ? (
            <CheckoutFormSkeleton />
          ) : (
            <CheckoutFormModern
              formData={formData}
              setFormData={handleChange}
              checkout={checkout}
            />
          )}

          {checkout?.exibirEndereco && (
            <DeliveryFormModern
              formData={formData}
              setFormData={handleChange}
              checkout={checkout}
            />
          )}
          {checkout?.orderBumps?.map((o) => (
            <OrderBump
              key={o.id}
              orderBump={o}
              isChecked={
                !!checkout?.orderBumpsAplicados?.find((_o) => _o.id === o.id)
              }
              onToggle={handleOrderBumpToggle}
              checkout={checkout}
            />
          ))}
          {isLoading ? (
            <PaymentMethodsSkeleton />
          ) : (
            <PaymentFormModern
              isLoading={isLoading}
              handlePurchaseClick={handlePurchaseClick}
              formData={formData}
              onFormDataChange={handleChange}
              checkout={checkout}
              setSelectedMethod={setPaymentMethod}
              selectedMethod={paymentMethod}
              mpCardRef={mpCardRef}
              numberRef={numberRef}
              cvcRef={cvcRef}
              expiryRef={expiryRef}
              handleStripeChange={handleStripeChange}
            />
          )}
          {checkout?.exibirCupomDesconto && (
            <CouponSection
              checkout={checkout}
              couponMessage={couponMessage}
              onApplyCoupon={onApplyCoupon}
              discount={discount}
            />
          )}
          <S.Footer>
            <img
              style={{ width: '99px', height: '99px' }}
              className="safe"
              src={SSL}
            />
            <img
              style={{ width: '69px', height: '69px' }}
              className="safe"
              src={SecurePayment}
            />
            <p>
              {t('recaptchaNotice')} <br />
              <strong>{t('privacyPolicy')}</strong> {t('and')}{' '}
              <strong>{t('termsOfService')}</strong>
            </p>
          </S.Footer>
        </div>
        {checkout?.checkoutConfig?.config?.componentsBelow?.map((component) =>
          renderAditionalComponent(component),
        )}
      </S.Page>
    </>
  );
};
